import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Loader2,
  Globe,
} from 'lucide-react';

import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/common/Logo';

function getSafeRedirect(redirectParam: string | null): string {
  if (!redirectParam) {
    return '/app';
  }

  // Only permit internal relative paths starting with a single slash.
  // This prevents open-redirect URLs such as //evil.com.
  if (
    redirectParam.startsWith('/') &&
    !redirectParam.startsWith('//')
  ) {
    return redirectParam;
  }

  return '/app';
}

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirectParam = searchParams.get('redirect');
  const emailParam = searchParams.get('email') || '';

  const { login } = useAuth();

  const [email, setEmail] = useState(emailParam);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handle login form submission.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || !password.trim()) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const res = await api.auth.login({
        email: email.trim().toLowerCase(),
        password,
      });

      await login({
        accessToken: res.accessToken,
        refreshToken: res.refreshToken,
      });

      navigate(getSafeRedirect(redirectParam));
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          'Invalid email or password. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fill the configured demo account credentials.
   */
  const handleDemoLogin = () => {
    setEmail('staff.architect@sahajconnect.com');
    setPassword('Password123!');
    setError(null);
  };

  /**
   * Build the registration URL while preserving redirect/email params.
   */
  const getRegisterUrl = (): string => {
    const params = new URLSearchParams();

    if (redirectParam) {
      params.set('redirect', redirectParam);
    }

    if (emailParam) {
      params.set('email', emailParam);
    }

    const query = params.toString();

    return query ? `/register?${query}` : '/register';
  };

  return (
    <div className="h-screen w-screen overflow-hidden flex flex-col lg:flex-row bg-[#F7F9FB] select-none text-[#1a1b4b]">
      
      {/* ============================================================
          LEFT PANEL: AUTHENTICATION FORM (40% on desktop)
          ============================================================ */}
      <div className="w-full lg:w-[40%] h-full p-6 sm:p-10 md:p-14 xl:p-16 flex flex-col justify-between bg-white overflow-y-auto z-10 flex-shrink-0 border-r border-[#3e4491]/20">
        {/* Top Logo */}
        <div>
          <Link
            to="/"
            className="inline-flex items-center group transition-transform hover:scale-[1.02]"
          >
            <Logo size="md" />
          </Link>
        </div>

        {/* Center Form Section */}
        <div className="my-auto py-6 max-w-md w-full mx-auto space-y-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-[#1a1b4b]">
              Welcome back
            </h1>
            <p className="text-xs sm:text-sm text-[#3e4491] font-medium mt-1.5">
              Welcome back! Please enter your details to sign in.
            </p>
          </div>

          {/* Error Banner */}
          {error && (
            <div
              role="alert"
              className="p-3.5 rounded-2xl bg-red-50 border border-red-200 text-red-600 text-xs font-medium animate-in fade-in slide-in-from-top-1"
            >
              {error}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Address */}
            <div>
              <label
                htmlFor="login-email"
                className="block text-xs font-bold text-[#1a1b4b] mb-1.5"
              >
                Email
              </label>

              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3e4491]" />

                <input
                  id="login-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  className="w-full h-11 pl-10 pr-3.5 bg-[#F8FAFC] border border-[#3e4491]/30 rounded-xl text-sm text-[#1a1b4b] focus:outline-none focus:ring-2 focus:ring-[#3a9efd]/20 focus:border-[#3a9efd] focus:bg-white transition shadow-2xs font-medium"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="login-password"
                className="block text-xs font-bold text-[#1a1b4b] mb-1.5"
              >
                Password
              </label>

              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3e4491]" />

                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="w-full h-11 pl-10 pr-10 bg-[#F8FAFC] border border-[#3e4491]/30 rounded-xl text-sm text-[#1a1b4b] focus:outline-none focus:ring-2 focus:ring-[#3a9efd]/20 focus:border-[#3a9efd] focus:bg-white transition shadow-2xs font-medium"
                />

                {/* Password Toggle Button */}
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#3e4491] hover:text-[#1a1b4b] transition focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Sign In Submit Button */}
            <button
              type="submit"
              disabled={
                isLoading ||
                !email.trim() ||
                !password.trim()
              }
              className="w-full h-11 mt-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#1a1b4b] hover:bg-[#292a73] text-white font-bold text-sm shadow-md shadow-[#1a1b4b]/20 transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 cursor-pointer"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-[#f7a400]" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign in</span>
                  <ArrowRight className="h-4 w-4 text-[#f7a400]" />
                </>
              )}
            </button>
          </form>

          {/* Quick Demo Credentials */}
          <div className="pt-3 border-t border-[#3e4491]/20 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleDemoLogin}
                className="text-[#292a73] hover:text-[#3a9efd] hover:underline font-bold text-xs transition"
              >
                Fill Demo User
              </button>
              <span className="text-[#3e4491]/40">•</span>
              <button
                type="button"
                onClick={() => {
                  setEmail('adminconnect@connect.com');
                  setPassword('password123');
                }}
                className="text-[#292a73] hover:text-[#3a9efd] hover:underline font-bold text-xs transition"
              >
                Fill Platform Admin
              </button>
            </div>

            <span className="text-[10px] font-mono text-[#3e4491] bg-[#3a9efd]/10 px-2 py-0.5 rounded-md border border-[#3a9efd]/20 w-fit font-bold">
              PostgreSQL 17 Auth
            </span>
          </div>
        </div>

        {/* Bottom Account Creation Link */}
        <div className="pt-4 text-center">
          <p className="text-xs text-[#3e4491] font-medium">
            Don't have an account?{' '}
            <Link
              to={getRegisterUrl()}
              className="font-bold text-[#1a1b4b] hover:text-[#3a9efd] transition underline underline-offset-4 decoration-[#3a9efd]/60"
            >
              Sign up for free
            </Link>
          </p>
        </div>
      </div>

      {/* ============================================================
          RIGHT PANEL: SAHAJ CONNECT BRANDING HERO (60% on desktop)
          ============================================================ */}
      <div className="hidden lg:flex lg:w-[60%] h-full relative overflow-hidden bg-[#1a1b4b] flex-col justify-between p-10 xl:p-14 select-none">
        {/* Background Hero Image */}
        <img
          src="/login-hero.jpg"
          alt="Sahaj Connect Team Collaboration"
          className="absolute inset-0 w-full h-full object-cover object-center filter brightness-[0.94] contrast-[1.03] transform scale-100 hover:scale-105 transition-transform duration-1000 ease-out"
        />

        {/* Subtle Modern Palette Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1b4b]/80 via-transparent to-[#1a1b4b]/50 pointer-events-none" />
        <div className="absolute inset-0 bg-[#292a73]/10 mix-blend-overlay pointer-events-none" />

        {/* Top Brand Badges */}
        <div className="relative z-10 flex items-center justify-between">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1a1b4b]/80 backdrop-blur-md border border-white/20 text-white text-xs font-bold shadow-lg">
            <span className="w-2 h-2 rounded-full bg-[#f7a400] animate-pulse" />
            <span>Sahaj Connect Workspace</span>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden xl:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white/90 text-[11px] font-medium">
              <Globe className="h-3 w-3 text-[#3a9efd]" />
              <span>Multilingual AI Translation</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};